import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { MAIN_SITE_URL } from "@/mocks/categories";
import JsonLd from "@/components/base/JsonLd";
import Header from "@/components/feature/Header";
import Footer from "@/components/feature/Footer";
import ComparisonTable from "@/components/feature/ComparisonTable";
import FaqAccordion from "@/components/feature/FaqAccordion";

const tocItems = [
  { id: "what-is", label: "article.toc.1" },
  { id: "why", label: "article.toc.2" },
  { id: "how", label: "article.toc.3" },
  { id: "comparison", label: "article.toc.4" },
  { id: "look-for", label: "article.toc.5" },
  { id: "faq", label: "article.toc.6" },
];

const steps = ["article.s3.1", "article.s3.2", "article.s3.3", "article.s3.4"];

const SITE_URL = import.meta.env.VITE_SITE_URL;
const LOGO_URL =
  "https://static.readdy.ai/image/2df560051ef0cafd8dabf8a049058ba4/2277e9ffbdee9fd651886f1aeeba0d56.jpeg";

const guideJsonLd = {
  "@context": "https://schema.org",
  "@type": "Article",
  headline: "USFans Spreadsheet: Complete Guide",
  description:
    "A complete USFans Spreadsheet guide covering what it is, why it helps, how to use it, and what to look for. Learn to explore and compare creator resources efficiently.",
  author: { "@type": "Organization", name: "USFansSheet" },
  publisher: {
    "@type": "Organization",
    name: "USFansSheet",
    logo: { "@type": "ImageObject", url: LOGO_URL },
  },
  mainEntityOfPage: `${SITE_URL}/usfans-spreadsheet-guide`,
  datePublished: "2026-08-20",
  dateModified: "2026-08-20",
  inLanguage: "en",
};

export default function Guide() {
  const { t } = useTranslation();

  const scrollTo = (id: string) => {
    document.getElementById(id)?.scrollIntoView({ behavior: "smooth" });
  };

  return (
    <div className="min-h-screen bg-background-50">
      <JsonLd data={guideJsonLd} />
      <Header />

      <main className="mx-auto max-w-3xl px-4 pb-20 pt-28 md:px-6 md:pt-32">
        {/* Breadcrumb */}
        <nav className="flex flex-wrap items-center gap-2 text-sm text-foreground-500">
          <Link to="/" className="transition-colors hover:text-primary-600">
            {t("nav.home")}
          </Link>
          <span>/</span>
          <span className="text-foreground-800">{t("nav.guide")}</span>
        </nav>

        <h1 className="mt-5 font-heading text-3xl font-bold leading-tight text-foreground-950 md:text-4xl">
          {t("article.h1")}
        </h1>

        {/* Featured snippet */}
        <div className="mt-6 rounded-lg border-l-4 border-primary-500 bg-primary-50 p-5">
          <p className="text-base font-medium leading-relaxed text-foreground-800">{t("article.short")}</p>
        </div>

        {/* Meta */}
        <div className="mt-5 flex flex-wrap items-center gap-x-5 gap-y-2 text-xs text-foreground-500">
          <span className="flex items-center gap-1.5">
            <i className="ri-user-line"></i>
            {t("article.meta.author")}
          </span>
          <span className="flex items-center gap-1.5">
            <i className="ri-time-line"></i>
            {t("article.meta.date")}
          </span>
        </div>

        <p className="mt-6 text-base leading-relaxed text-foreground-700">{t("article.intro")}</p>

        {/* Table of contents */}
        <div className="mt-8 rounded-lg border border-background-200 bg-background-50 p-6">
          <h2 className="font-heading text-base font-semibold text-foreground-950">{t("article.toc.title")}</h2>
          <ol className="mt-4 space-y-2">
            {tocItems.map((item, idx) => (
              <li key={item.id}>
                <button
                  type="button"
                  onClick={() => scrollTo(item.id)}
                  className="flex items-center gap-3 text-sm text-foreground-700 transition-colors hover:text-primary-600"
                >
                  <span className="flex h-6 w-6 shrink-0 items-center justify-center rounded-full bg-primary-100 text-xs font-semibold text-primary-700">
                    {idx + 1}
                  </span>
                  {t(item.label)}
                </button>
              </li>
            ))}
          </ol>
        </div>

        {/* Section 1 */}
        <section id="what-is" className="mt-12 scroll-mt-24">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">{t("article.s1.title")}</h2>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("article.s1.p")}</p>
        </section>

        {/* Section 2 */}
        <section id="why" className="mt-12 scroll-mt-24">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">{t("article.s2.title")}</h2>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("article.s2.p")}</p>
        </section>

        {/* Section 3 */}
        <section id="how" className="mt-12 scroll-mt-24">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">{t("article.s3.title")}</h2>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("article.s3.intro")}</p>
          <ol className="mt-6 space-y-4">
            {steps.map((s, idx) => (
              <li key={s} className="flex gap-4">
                <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-accent-500 text-sm font-bold text-background-50">
                  {idx + 1}
                </span>
                <p className="text-base leading-relaxed text-foreground-700">{t(s)}</p>
              </li>
            ))}
          </ol>
        </section>

        {/* Section 4 */}
        <section id="comparison" className="mt-12 scroll-mt-24">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">{t("article.s4.title")}</h2>
          <div className="mt-6">
            <ComparisonTable />
          </div>
        </section>

        {/* Section 5 */}
        <section id="look-for" className="mt-12 scroll-mt-24">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">{t("article.s5.title")}</h2>
          <p className="mt-4 text-base leading-relaxed text-foreground-700">{t("article.s5.p")}</p>
        </section>

        {/* FAQ */}
        <section id="faq" className="mt-12 scroll-mt-24">
          <h2 className="font-heading text-2xl font-bold text-foreground-950">{t("faq.title")}</h2>
          <div className="mt-6">
            <FaqAccordion />
          </div>
        </section>

        {/* Related + CTA */}
        <div className="mt-12 rounded-lg bg-primary-50 p-8 text-center">
          <p className="text-base leading-relaxed text-foreground-700">{t("article.related")}</p>
          <div className="mt-6 flex flex-col items-center justify-center gap-3 sm:flex-row">
            <Link
              to="/"
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md border border-primary-300 px-6 py-3 text-sm font-semibold text-primary-700 transition-colors hover:bg-primary-100 sm:w-auto"
            >
              <i className="ri-arrow-left-line"></i>
              {t("article.back")}
            </Link>
            <a
              href={MAIN_SITE_URL}
              target="_blank"
              rel="noopener nofollow"
              className="inline-flex w-full items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-6 py-3 text-sm font-semibold text-background-50 transition-colors hover:bg-primary-600 sm:w-auto"
            >
              {t("article.cta")}
              <i className="ri-arrow-right-up-line"></i>
            </a>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
}